/**
 * Topbar — 44px persistent bar.
 *
 * Spec: Docs/design/04-shell-and-navigation.md §4
 *
 * Logo · Search (Cmd+K) · Agents+Cost · Connection pip · Bell · Avatar
 *
 * The search button cycles through animated placeholder hints and opens
 * the CommandPalette overlay on click or Cmd+K.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/DropdownMenu";
import { ConnectionPip } from "./ConnectionPip";
import { useAuth } from "@/auth/useAuth";
import { CostMeter } from "./CostMeter";
import { InputBadge } from "./InputBadge";
import { NotificationBell } from "@/components/layout/NotificationBell";

const SEARCH_HINTS = [
  "Search commands…",
  "Go to agents…",
  "Open settings…",
  "Ask Omni anything…",
  "Search memory…",
  "Navigate to runs…",
  "Toggle theme…",
  "Find a skill…",
  "Go to inbox…",
  "Search tasks…",
];

interface TopbarProps {
  onMenuClick: () => void;
  sidebarCollapsed: boolean;
  onSearchClick: () => void;
}

export function Topbar({ onMenuClick, onSearchClick }: TopbarProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const initial = auth.user?.displayName?.[0]?.toUpperCase()
    ?? auth.user?.email?.[0]?.toUpperCase()
    ?? "U";

  const [hintIdx, setHintIdx] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setHintIdx((i) => (i + 1) % SEARCH_HINTS.length);
    }, 3400);
    return () => clearInterval(timer);
  }, []);



  return (
    <header
      className="flex h-[44px] items-center border-b border-border bg-bg-elevated px-4 gap-4"
      role="banner"
    >
      {/* Mobile menu */}
      <button
        type="button"
        className="md:hidden text-fg-muted hover:text-fg"
        onClick={onMenuClick}
        aria-label="Toggle navigation"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>

      {/* Search bar — opens CommandPalette */}
      <div className="flex-1 max-w-sm">
        <button
          type="button"
          onClick={onSearchClick}
          className={cn(
            "flex items-center gap-2 h-7 w-full px-3 text-xs text-fg-faint",
            "border border-border bg-bg-sunken hover:border-border-strong transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--om-color-focus-ring)]",
          )}
          aria-label="Open command palette (Cmd+K)"
          aria-keyshortcuts="Meta+k Control+k"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="flex-shrink-0"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          {/* Animated cycling hint */}
          <span
            key={hintIdx}
            className="hidden sm:inline om-fade-in-up truncate flex-1 text-left"
          >
            {SEARCH_HINTS[hintIdx]}
          </span>
          <kbd className="hidden sm:inline ml-auto font-mono text-[10px] text-fg-faint border border-border px-1 flex-shrink-0">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* HITL Input Badge */}
        <InputBadge />

        {/* Cost progress meter */}
        <CostMeter />

        {/* Connection pip */}
        <SimpleTooltip label="Connected to Omni">
          <ConnectionPip status="connected" />
        </SimpleTooltip>

        {/* Notifications — Omni's own inbox, backed by
            GET /api/v1/notifications. The vendor Inbox widget it
            replaced added a second notification store that the
            in_app channel already owned. */}
        <SimpleTooltip label="Notifications">
          <NotificationBell />
        </SimpleTooltip>

        {/* Theme toggle */}
        <SimpleTooltip label="Toggle theme">
          <ThemeToggle iconOnly className="h-7 px-2 text-[10px]" />
        </SimpleTooltip>

        {/* Avatar Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="w-7 h-7 flex items-center justify-center bg-accent text-accent-fg text-xs font-semibold rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--om-color-focus-ring)]"
          >
            {initial}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-3 py-2 border-b border-border mb-1">
              <div className="text-sm font-medium text-fg truncate">
                {auth.user?.displayName || "User"}
              </div>
              <div className="text-xs text-fg-muted truncate">
                {auth.user?.email || "No email"}
              </div>
            </div>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              Profile & Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/usage")}>
              Usage & Billing
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void auth.signOut()}>
              <span className="text-danger">Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
